import React, { useState, useRef } from 'react';
import { HomeIcon, MessageIcon, UsersIcon, UserPlusIcon, LogOutIcon, CalendarIcon, MenuIcon, CloseIcon, WhatsAppIcon, ShieldIcon, LinkIcon, FileTextIcon, UserIcon, MediaIcon, MusicIcon, BookIcon, TrashIcon } from './icons/Icons';
import { CONFIG } from '../config';

const Navigation = ({ currentPath, onNavigate, isAuthenticated, isAdmin, userName, activeEventId, availableEvents, allowedChannels = 'both' }) => {
  const smsEnabled = allowedChannels === 'sms' || allowedChannels === 'both';
  const waEnabled = allowedChannels === 'whatsapp' || allowedChannels === 'both';
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const closeTimeoutRef = useRef(null);

  const navStructure = isAuthenticated ? [
    { path: '/home', label: 'Home', icon: HomeIcon },
    {
      label: 'Messages',
      icon: MessageIcon,
      children: [
        ...(smsEnabled ? [{ path: '/message', label: 'SMS Message', icon: MessageIcon }] : []),
        ...(waEnabled ? [{ path: '/whatsapp-message', label: 'WhatsApp Message', icon: WhatsAppIcon }] : []),
        { path: '/scheduled-messages', label: 'Scheduled Messages', icon: CalendarIcon },
        { path: '/heydj', label: '#HeyDJ', icon: MusicIcon },
      ]
    },
    {
      label: 'Guests',
      icon: UsersIcon,
      children: [
        { path: '/list-guests', label: 'List Guests', icon: UsersIcon },
        { path: '/add-guest', label: 'Add Guest', icon: UserPlusIcon },
        ...(isAdmin ? [{ path: '/bulk-add-guests', label: 'Bulk Add Guests', icon: UserPlusIcon }] : []),
      ]
    },
    { path: '/events', label: 'Events', icon: CalendarIcon },
    ...(isAdmin ? [{
      label: 'Admin',
      icon: ShieldIcon,
      children: [
        { path: '/admin', label: 'Admin Dashboard', icon: ShieldIcon },
        { path: '/admin/events', label: 'Event Management', icon: CalendarIcon },
        { path: '/admin/templates', label: 'Message Templates', icon: FileTextIcon },
        { path: '/admin/self-registration', label: 'Self-Registration', icon: LinkIcon },
        { path: '/admin/inbound-messages', label: 'Messages Log', icon: MessageIcon },
        { path: '/admin/media', label: 'Media', icon: MediaIcon },
        { path: '/admin/knowledge-base', label: 'Knowledge Base', icon: BookIcon },
        { path: '/admin/ai-config', label: 'AI Configuration', icon: FileTextIcon },
        { path: '/admin/ai-test', label: 'AI Tester', icon: MessageIcon },
        { path: '/admin/delete-event-components', label: 'Delete Components', icon: TrashIcon },
      ]
    }] : []),
  ] : [];

  const handleNavigate = (path) => {
    onNavigate(path);
    setMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const isItemActive = (item) => {
    if (item.path) {
      return currentPath === item.path;
    }
    if (item.children) {
      return item.children.some(child => currentPath === child.path);
    }
    return false;
  };

  const handleDropdownEnter = (label) => {
    // Clear any pending close timeout
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setOpenDropdown(label);
  };

  const handleDropdownLeave = () => {
    // Delay closing by 1.5 seconds
    closeTimeoutRef.current = setTimeout(() => {
      setOpenDropdown(null);
    }, 1500);
  };

  return (
    <header className="navbar">
      <div className="navbar-container">
        <h1 className="navbar-title">PandA Notify</h1>
        {isAuthenticated && availableEvents?.find(ev => ev.event_id === activeEventId)?.event_name && (
          <span className="nav-event-label">
            {availableEvents.find(ev => ev.event_id === activeEventId).event_name}
          </span>
        )}

        <button
          className="mobile-menu-toggle"
          onClick={toggleMobileMenu}
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
        </button>

        <nav className={`navbar-links ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          {navStructure.map((item, index) => {
            if (item.children) {
              // Dropdown menu item
              const isOpen = openDropdown === item.label;
              return (
                <div
                  key={index}
                  className={`nav-dropdown ${isItemActive(item) ? 'active' : ''} ${isOpen ? 'open' : ''}`}
                  onMouseEnter={() => handleDropdownEnter(item.label)}
                  onMouseLeave={handleDropdownLeave}
                >
                  <button className="nav-button nav-dropdown-toggle">
                    <item.icon />
                    {item.label}
                  </button>
                  <div className="nav-dropdown-menu">
                    {item.children.map((child) => (
                      <button
                        key={child.path}
                        onClick={() => handleNavigate(child.path)}
                        className={`nav-dropdown-item ${currentPath === child.path ? 'active' : ''}`}
                      >
                        <child.icon />
                        {child.label}
                      </button>
                    ))}
                  </div>
                </div>
              );
            } else {
              // Regular nav item
              return (
                <button
                  key={item.path}
                  onClick={() => handleNavigate(item.path)}
                  className={`nav-button ${currentPath === item.path ? 'active' : ''}`}
                >
                  <item.icon />
                  {item.label}
                </button>
              );
            }
          })}
          {isAuthenticated ? (
            <div
              className={`nav-dropdown nav-user-section ${currentPath === '/profile' ? 'active' : ''} ${openDropdown === '_user' ? 'open' : ''}`}
              onMouseEnter={() => handleDropdownEnter('_user')}
              onMouseLeave={handleDropdownLeave}
            >
              <button className="nav-button nav-dropdown-toggle">
                <UserIcon />
                {userName || 'Account'}
              </button>
              <div className="nav-dropdown-menu nav-dropdown-menu-right">
                <button
                  onClick={() => handleNavigate('/profile')}
                  className={`nav-dropdown-item ${currentPath === '/profile' ? 'active' : ''}`}
                >
                  <UserIcon />
                  Profile
                </button>
                <button
                  onClick={() => handleNavigate('/user-guide')}
                  className={`nav-dropdown-item ${currentPath === '/user-guide' ? 'active' : ''}`}
                >
                  <BookIcon />
                  User Guide
                </button>
                <a href={CONFIG.cognitoLogoutUrl} className="nav-dropdown-item">
                  <LogOutIcon />
                  Logout
                </a>
              </div>
            </div>
          ) : (
            <a href={CONFIG.cognitoLoginUrl} className="nav-link">
              Login
            </a>
          )}
        </nav>
      </div>
    </header>
  );
};

export default Navigation;